/* eslint-disable no-console */
import { LogLevel } from '@amplitude/analytics-browser/lib/cjs/types';
import { AnalyticsProvider, IdentityPayload, TrackProps } from '../manager';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

export class AmplitudeProvider implements AnalyticsProvider {
  private isInitialized = false;
  private apiKey: string = '';

  public init(options: { apiKey: string; debug?: boolean }): void {
    if (!options.apiKey) {
      console.debug('Amplitude: No API key provided, skipping initialization');
      return;
    }

    this.apiKey = options.apiKey;

    try {
      amplitude.add(sessionReplayPlugin({ debugMode: options.debug }));

      amplitude.init(this.apiKey, {
        logLevel: options.debug ? LogLevel.Debug : LogLevel.Warn,
        defaultTracking: {
          sessions: true,
          attribution: true,
          formInteractions: false,
          pageViews: false,
        },
      });

      this.isInitialized = true;
    } catch (error) {
      console.debug('Failed to initialize Amplitude:', error);
    }
  }

  public identify(identity: IdentityPayload): void {
    if (!this.isReady()) return;

    // User ID is used to identify the user in Amplitude
    amplitude.setUserId(identity.userId);

    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('email', identity.email);
    if (identity.firstName) identifyEvent.set('first_name', identity.firstName);
    if (identity.lastName) identifyEvent.set('last_name', identity.lastName);

    if (identity.properties) {
      Object.entries(identity.properties).forEach(([key, value]) => {
        identifyEvent.set(key, value);
      });
    }

    amplitude.identify(identifyEvent);
  }

  public track(event: string, properties?: TrackProps): void {
    if (!this.isReady()) return;

    // Used for group-level analytics
    if (properties?.orgId) amplitude.setGroup('organization_id', properties.orgId);
    if (properties?.eventId) amplitude.setGroup('event_id', properties.eventId);

    amplitude.track(event, { ...properties });
  }

  public isReady(): boolean {
    return this.isInitialized;
  }
}
